import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FaUserShield, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import api from '../../api/axios';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { login } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const savedEmail = localStorage.getItem('admin_email');
    if (savedEmail) setUsername(savedEmail);
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!username || !password) {
      setError(t('auth.error_empty'));
      return;
    }

    setLoading(true);
    try {
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);

      const res = await api.post('/auth/login/admin', formData.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      const data = res.data;

      // 确保后端返回的是 admin 身份
      if (res.status === 200 && data.access_token && data.role === 'admin') {
        login(data.access_token, 'admin', 'admin');
        localStorage.setItem('admin_email', username);
        navigate('/admin/dashboard');
      } else {
        setError(t('admin.common.access_denied')); // 非 admin 用户強制ログイン
        localStorage.removeItem('token');
        localStorage.removeItem('role');
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      const i18nKey = code ? `auth.${code.toLowerCase()}` : null;

      if (status === 401 || status === 403) {
        setError(i18nKey && t(i18nKey) !== i18nKey ? t(i18nKey) : t('auth.error_unauthorized'));
      } else {
        setError(t('auth.error_network'));
      }

      localStorage.removeItem('token');
      localStorage.removeItem('role');
    } finally {
      setLoading(false);
    }
  };

  const shakeAnimation = {
    x: [0, -10, 10, -10, 10, 0],
    transition: { duration: 0.6 },
  };

  return (
    <div
      className="min-h-screen bg-background bg-gradient-hero text-base"
    >
      <Helmet>
        <title>{t('auth.admin_login_title')} - Melodis Clinic</title>
      </Helmet>

      {/* 顶部导航栏 */}
      <div className="flex justify-between items-center px-4 sm:px-6 py-4 bg-white bg-opacity-90 shadow">
        <div className="flex items-center gap-4">
          <img
            src="/images/Melodislogo.png"
            alt="Logo"
            className="h-10 cursor-pointer"
            onClick={() => navigate('/login/admin')}
          />
          <h1 className="text-xl sm:text-2xl font-bold">{t('admin.title')}</h1>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => i18n.changeLanguage('ja')} className="text-sm underline">日本語</button>
          <button onClick={() => i18n.changeLanguage('zh')} className="text-sm underline">中文</button>
        </div>
      </div>

      {/* 登录表单 */}
      <div className="flex justify-center px-4 mt-10">
        <motion.div
          className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md"
          animate={error ? shakeAnimation : {}}
        >
          <h2 className="text-2xl font-bold mb-6 text-center text-coral">
            {t('auth.admin_login_title')}
          </h2>

          {error && (
            <div className="text-red-500 mb-4 text-center">
              {error}
              <p className="text-sm text-gray-500 mt-1">{t('auth.try_again')}</p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-coral transition">
              <FaUserShield className="text-gray-400 mr-2" />
              <input
                type="text"
                placeholder={t('auth.username')}
                className="w-full outline-none text-gray-700"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>

            <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-coral transition relative">
              <FaLock className="text-gray-400 mr-2" />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.password')}
                className="w-full outline-none text-gray-700 pr-8"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 cursor-pointer text-gray-500"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 rounded-full font-bold shadow-soft transition-all ${
                loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-coral text-white hover:shadow-card hover:-translate-y-0.5 active:scale-95'
              }`}
            >
              {loading ? t('auth.logging_in') : t('auth.login_button')}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminLogin;
